import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
  TextField,
} from "@mui/material";
import { Link, useParams, useNavigate } from "react-router-dom";
import { faqApi } from "../../services/api";
import type { Faq } from "../../models";
import SeoEditor from "../../components/cms/SeoEditor";

const FaqEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [faq, setFaq] = useState<Faq | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  const isNewFaq = id === "new";

  useEffect(() => {
    const fetchFaq = async () => {
      try {
        setLoading(true);
        if (id && id !== "new") {
          const response = await faqApi.getById(parseInt(id, 10));
          setFaq(response.data);
        } else {
          // Initialize new FAQ
          setFaq({
            id: 0,
            question: "",
            answer: "",
            order: 0,
            seo: {},
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        }
      } catch (error) {
        console.error("Error fetching FAQ:", error);
        setNotification({
          open: true,
          message: "Failed to load FAQ data",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchFaq();
  }, [id]);

  const handleInputChange =
    (field: keyof Faq) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (faq) {
        setFaq({
          ...faq,
          [field]: event.target.value,
        });
      }
    };

  const handleSeoChange = (seo: any) => {
    if (faq) {
      setFaq({
        ...faq,
        seo,
      });
    }
  };

  const handleSave = async () => {
    if (!faq) return;

    try {
      setSaving(true);
      let savedFaq;

      if (id && id !== "new") {
        savedFaq = await faqApi.update(parseInt(id, 10), faq);
      } else {
        savedFaq = await faqApi.create(faq);
      }

      setNotification({
        open: true,
        message: "FAQ saved successfully",
        severity: "success",
      });

      // Redirect to edit page with the new ID if this was a new FAQ
      if (isNewFaq && savedFaq.data && savedFaq.data.id) {
        navigate(`/faqs/${savedFaq.data.id}`, { replace: true });
      }
    } catch (error) {
      console.error("Error saving FAQ:", error);
      setNotification({
        open: true,
        message: "Failed to save FAQ",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!faq) {
    return (
      <Paper sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          FAQ not found
        </Typography>
        <Button
          component={Link}
          to="/cms/faqs"
          variant="contained"
          sx={{ mt: 2 }}
        >
          Back to FAQs
        </Button>
      </Paper>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink component={Link} to="/cms/faqs" underline="hover">
          FAQs
        </MuiLink>
        <Typography color="text.primary">
          {id && id !== "new" ? `Edit FAQ #${id}` : "New FAQ"}
        </Typography>
      </Breadcrumbs>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h5">
            {id && id !== "new" ? `Edit FAQ #${id}` : "New FAQ"}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : "Save FAQ"}
          </Button>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Question"
            value={faq.question}
            onChange={handleInputChange("question")}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Answer"
            value={faq.answer}
            onChange={handleInputChange("answer")}
            multiline
            rows={4}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Order"
            type="number"
            value={faq.order}
            onChange={handleInputChange("order")}
            sx={{ mb: 2 }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            SEO Information
          </Typography>
          <SeoEditor
            seo={faq.seo || {}}
            onChange={handleSeoChange}
          />
        </Box>
      </Paper>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FaqEditor;
